import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api, { errMsg } from '@/lib/api';
import { money2 } from '@/lib/helpers';
import { useApp } from '@/context/AppContext';
import { CustomFieldsForm } from '@/components/CustomFieldsForm';
import { fieldsFor, cleanCustomValues } from '@/lib/customFields';
import { toast } from 'sonner';
import { Loader2, Trash2, Plus, Save, X, CalendarClock } from 'lucide-react';

const METHODS = ['CASH', 'UPI', 'CARD', 'BANK', 'CREDIT'];

export default function InvoiceEdit() {
  const { invoiceNo } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addCode, setAddCode] = useState('');
  const [adding, setAdding] = useState(false);

  const { settings, user } = useApp();
  const { data: staff = [] } = useQuery({
    queryKey: ['inc-staff-edit'],
    queryFn: async () => (await api.get('/incentives/staff')).data.data,
  });
  const staffOptions = useMemo(() => {
    const names = (staff || []).map((s) => s.name).filter(Boolean);
    const seen = new Set(names.map((n) => n.toLowerCase()));
    const me = user?.name || user?.username;
    if (me && !seen.has(me.toLowerCase())) { names.unshift(me); seen.add(me.toLowerCase()); }
    return names;
  }, [staff, user]);
  const billingFields = fieldsFor(settings?.custom_fields, 'billing');
  const [items, setItems] = useState([]);
  const [billDiscount, setBillDiscount] = useState('0');
  const [payments, setPayments] = useState([{ method: 'CASH', amount: '' }]);
  const [customer, setCustomer] = useState({ name: '', phone: '' });
  const [salesperson, setSalesperson] = useState('');
  const [dateStr, setDateStr] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [customVals, setCustomVals] = useState({});

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const s = (await api.get(`/sales/${invoiceNo}`)).data.data;
        setItems((s.items || []).map((i) => ({
          barcode: i.barcode, product: i.product, size: i.size || '', colour: i.colour || '',
          price: String(i.price ?? 0), discount: String(i.discount ?? 0),
          garment_id: i.garment_id, cost: i.cost, returned: i.returned,
        })));
        setBillDiscount(String(s.bill_discount ?? 0));
        setPayments((s.payments && s.payments.length ? s.payments : [{ method: 'CASH', amount: s.net }]).map((p) => ({ method: p.method, amount: String(p.amount ?? '') })));
        setCustomer({ name: s.customer?.name || '', phone: s.customer?.phone || '' });
        setSalesperson(s.salesperson || '');
        setDateStr((s.created_at || '').slice(0, 10));
        setDueDate((s.due_date || '').slice(0, 10));
        setNotes(s.notes || '');
        setCustomVals(s.custom || {});
      } catch (e) { toast.error(errMsg(e)); }
      finally { setLoading(false); }
    })();
  }, [invoiceNo]);

  const lineFinal = (it) => Math.max(0, Number(it.price || 0) - Number(it.discount || 0));
  const gross = useMemo(() => items.reduce((a, it) => a + Number(it.price || 0), 0), [items]);
  const itemDisc = useMemo(() => items.reduce((a, it) => a + Number(it.discount || 0), 0), [items]);
  const net = useMemo(() => Math.max(0, gross - itemDisc - Number(billDiscount || 0)), [gross, itemDisc, billDiscount]);
  const payTotal = useMemo(() => payments.reduce((a, p) => a + Number(p.amount || 0), 0), [payments]);

  const setItem = (idx, key, val) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, [key]: val } : it)));
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const addItem = async () => {
    const bc = addCode.trim().toUpperCase();
    if (!bc) return;
    if (items.some((it) => (it.barcode || '').toUpperCase() === bc)) { toast.error('That item is already on this bill.'); return; }
    setAdding(true);
    try {
      const g = (await api.get(`/garments/lookup/${encodeURIComponent(bc)}`)).data.data;
      setItems((arr) => [...arr, { barcode: g.barcode, product: g.product, size: g.size || '', colour: g.colour || '', price: String(g.price ?? 0), discount: '0', garment_id: g.id, cost: g.cost }]);
      setAddCode('');
    } catch (e) {
      // allow free-text item even if not found in inventory
      setItems((arr) => [...arr, { barcode: bc, product: bc, size: '', colour: '', price: '0', discount: '0', garment_id: null, cost: null }]);
      toast.message(`${bc} not in stock — added as a manual line.`);
      setAddCode('');
    } finally { setAdding(false); }
  };

  const setPay = (idx, key, val) => setPayments((arr) => arr.map((p, i) => (i === idx ? { ...p, [key]: val } : p)));
  const addPay = () => setPayments((arr) => [...arr, { method: 'CASH', amount: '' }]);
  const removePay = (idx) => setPayments((arr) => arr.filter((_, i) => i !== idx));
  const fillPayment = () => setPayments([{ method: payments[0]?.method || 'CASH', amount: String(net) }]);

  const save = async () => {
    if (items.length === 0) { toast.error('An invoice must have at least one item.'); return; }
    const payload = {
      items: items.map((it) => ({
        barcode: it.barcode, product: it.product, size: it.size, colour: it.colour,
        price: Number(it.price || 0), discount: Number(it.discount || 0), final: lineFinal(it),
        garment_id: it.garment_id, cost: it.cost, returned: !!it.returned,
      })),
      bill_discount: Number(billDiscount || 0),
      payments: payments.filter((p) => Number(p.amount || 0) > 0 || p.method === 'CREDIT').map((p) => ({ method: p.method, amount: Number(p.amount || 0) })),
      customer: (customer.name || customer.phone) ? { name: customer.name, phone: customer.phone } : null,
      salesperson: salesperson || undefined,
      created_at: dateStr ? `${dateStr}T12:00:00+00:00` : undefined,
      due_date: dueDate || null,
      notes: notes || '',
      custom: cleanCustomValues(settings?.custom_fields, 'billing', customVals),
    };
    if (payload.payments.length === 0) payload.payments = [{ method: 'CASH', amount: net }];
    setSaving(true);
    try {
      await api.put(`/sales/${invoiceNo}`, payload);
      toast.success('Invoice updated');
      navigate(`/invoice/${invoiceNo}`, { replace: true });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <Page noNav><TopBar title="Edit Invoice" back showStore={false} /><div className="flex justify-center pt-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div></Page>;

  return (
    <Page noNav>
      <TopBar title={`Edit Invoice ${invoiceNo}`} back showStore={false} />
      <div className="mx-auto max-w-3xl px-4 pt-4 pb-28">
        {/* Meta */}
        <div className="grid grid-cols-2 gap-3 rounded-2xl border border-border bg-card p-4">
          <div><Label className="text-xs text-muted-foreground">Customer name</Label>
            <Input value={customer.name} onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))} className="mt-1 h-10 rounded-lg" data-testid="invedit-cust-name" placeholder="Cash Sale" /></div>
          <div><Label className="text-xs text-muted-foreground">Customer phone</Label>
            <Input value={customer.phone} onChange={(e) => setCustomer((c) => ({ ...c, phone: e.target.value }))} className="mt-1 h-10 rounded-lg" data-testid="invedit-cust-phone" placeholder="-" /></div>
          <div><Label className="text-xs text-muted-foreground">Sold by</Label>
            <Select value={salesperson || undefined} onValueChange={setSalesperson}>
              <SelectTrigger className="mt-1 h-10 rounded-lg" data-testid="invedit-salesperson"><SelectValue placeholder="Select salesperson" /></SelectTrigger>
              <SelectContent>
                {salesperson && !staffOptions.some((n) => n.toLowerCase() === salesperson.toLowerCase()) && <SelectItem value={salesperson}>{salesperson}</SelectItem>}
                {staffOptions.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div><Label className="text-xs text-muted-foreground">Invoice date</Label>
            <Input type="date" value={dateStr} onChange={(e) => setDateStr(e.target.value)} className="mt-1 h-10 rounded-lg" data-testid="invedit-date" /></div>
        </div>

        {/* Delivery due + notes + custom additional details */}
        <div className="mt-4 rounded-2xl border border-border bg-card p-4">
          <div className="mb-3 flex items-center gap-2"><CalendarClock className="h-4 w-4 text-primary" /><p className="text-sm font-semibold">Delivery / follow-up &amp; notes</p></div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <Label className="text-xs text-muted-foreground">Delivery / alteration due date</Label>
              <div className="mt-1 flex items-center gap-2">
                <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="h-10 rounded-lg" data-testid="invedit-due-date" />
                {dueDate && <Button type="button" variant="ghost" size="sm" className="h-9 rounded-lg text-xs" onClick={() => setDueDate('')} data-testid="invedit-due-clear">Clear</Button>}
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">Set a date to get a reminder and track it under Due &amp; Reminders.</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Notes (prints on invoice)</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Sleeve alteration, pickup after 6pm" className="mt-1 h-10 rounded-lg" data-testid="invedit-notes" />
            </div>
          </div>
          {billingFields.length > 0 && (
            <div className="mt-3 border-t border-border pt-3">
              <p className="mb-2 text-xs font-semibold text-muted-foreground">Additional details</p>
              <CustomFieldsForm defs={settings?.custom_fields} context="billing" value={customVals}
                onChange={setCustomVals} idPrefix="invedit-cf" />
            </div>
          )}
        </div>

        {/* Items */}
        <div className="mt-4 rounded-2xl border border-border bg-card p-4">
          <div className="mb-3 flex items-center gap-2">
            <Input value={addCode} onChange={(e) => setAddCode(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }}
              className="h-10 rounded-lg font-mono" placeholder="Add item by code…" data-testid="invedit-add-code" />
            <Button className="h-10 rounded-lg" onClick={addItem} disabled={adding} data-testid="invedit-add-btn">
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" />Add</>}
            </Button>
          </div>
          <div className="space-y-2" data-testid="invedit-items">
            {items.map((it, idx) => (
              <div key={idx} className="grid grid-cols-12 items-center gap-2 rounded-xl border border-border p-2" data-testid={`invedit-item-${it.barcode}`}>
                <div className="col-span-5 min-w-0">
                  <p className="truncate text-sm font-medium">{it.product}</p>
                  <p className="font-mono text-xs text-muted-foreground">{it.barcode}</p>
                </div>
                <div className="col-span-3">
                  <Label className="text-[10px] text-muted-foreground">Price</Label>
                  <Input inputMode="numeric" value={it.price} onChange={(e) => setItem(idx, 'price', e.target.value)} className="h-9 rounded-lg" data-testid={`invedit-price-${it.barcode}`} />
                </div>
                <div className="col-span-3">
                  <Label className="text-[10px] text-muted-foreground">Discount</Label>
                  <Input inputMode="numeric" value={it.discount} onChange={(e) => setItem(idx, 'discount', e.target.value)} className="h-9 rounded-lg" data-testid={`invedit-disc-${it.barcode}`} />
                </div>
                <div className="col-span-1 flex justify-end">
                  <Button size="sm" variant="ghost" className="h-9 w-9 rounded-lg p-0 text-destructive" onClick={() => removeItem(idx)} data-testid={`invedit-remove-${it.barcode}`} aria-label="Remove"><Trash2 className="h-4 w-4" /></Button>
                </div>
                <div className="col-span-12 -mt-1 text-right text-xs text-muted-foreground">Line total: <span className="font-mono">{money2(lineFinal(it))}</span></div>
              </div>
            ))}
            {items.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No items. Add at least one.</p>}
          </div>
        </div>

        {/* Totals + payments */}
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">{money2(gross)}</span></div>
            <div className="mt-2 flex items-center justify-between gap-2 text-sm">
              <span className="text-muted-foreground">Item discounts</span><span className="font-mono">- {money2(itemDisc)}</span>
            </div>
            <div className="mt-2 flex items-center justify-between gap-2">
              <Label className="text-sm text-muted-foreground">Bill discount</Label>
              <Input inputMode="numeric" value={billDiscount} onChange={(e) => setBillDiscount(e.target.value)} className="h-9 w-28 rounded-lg text-right" data-testid="invedit-billdisc" />
            </div>
            <div className="mt-3 flex items-center justify-between border-t border-border pt-3 text-base font-bold">
              <span>Total</span><span className="font-mono" data-testid="invedit-net">{money2(net)}</span>
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-medium">Payments</p>
              <Button size="sm" variant="ghost" className="h-7 rounded-lg px-2 text-xs" onClick={fillPayment} data-testid="invedit-fill-pay">Match total</Button>
            </div>
            <div className="space-y-2">
              {payments.map((p, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Select value={p.method} onValueChange={(v) => setPay(idx, 'method', v)}>
                    <SelectTrigger className="h-9 w-32 rounded-lg" data-testid={`invedit-pay-method-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input inputMode="numeric" value={p.amount} onChange={(e) => setPay(idx, 'amount', e.target.value)} className="h-9 flex-1 rounded-lg text-right" placeholder="0" data-testid={`invedit-pay-amount-${idx}`} />
                  {payments.length > 1 && <Button size="sm" variant="ghost" className="h-9 w-9 rounded-lg p-0 text-destructive" onClick={() => removePay(idx)}><X className="h-4 w-4" /></Button>}
                </div>
              ))}
            </div>
            <Button size="sm" variant="secondary" className="mt-2 h-8 rounded-lg" onClick={addPay} data-testid="invedit-add-pay"><Plus className="mr-1 h-3.5 w-3.5" />Split payment</Button>
            <div className={`mt-3 flex items-center justify-between border-t border-border pt-3 text-sm ${Math.abs(payTotal - net) > 0.5 ? 'text-amber-600' : 'text-emerald-600'}`}>
              <span>Paid</span><span className="font-mono">{money2(payTotal)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky save bar */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 p-3 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-3">
          <Button variant="secondary" className="h-12 flex-1 rounded-xl" onClick={() => navigate(`/invoice/${invoiceNo}`)} disabled={saving}>Cancel</Button>
          <Button className="h-12 flex-[2] rounded-xl" onClick={save} disabled={saving} data-testid="invedit-save">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Save className="mr-2 h-5 w-5" />Save Invoice · {money2(net)}</>}
          </Button>
        </div>
      </div>
    </Page>
  );
}
